import React from 'react'
import type { PresentationIsEmptyProps, PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'

/**
 * The shape a v1 partner module exports. `isEmpty` is deliberately loose: v1 types it with v1's own
 * PresentationIsEmptyProps (which has `section`, not `block`), and the adapter discards it anyway —
 * partner content is static, so it's never empty.
 */
interface V1PartnerSectionModule {
  Section: React.ComponentType<any>
  isEmpty: (props: any) => boolean
}

/**
 * Wraps a v1 partner-content block module (Sherwin-Williams / Benjamin Moore) into the v2
 * registration contract (#21311). The v1 partner blocks are static marketing content — they
 * read no block.fields and only use `block`/`classes` from their props — so v2 reuses them
 * directly instead of duplicating them. Unlike native v2 blocks they don't render
 * `WrapperComponent` themselves, so the adapter wraps them.
 */
export default function createPartnerBlockModule(v1Module: V1PartnerSectionModule) {
  const V1Section = v1Module.Section

  function PartnerBlock(props: PresentationBlockProps) {
    const { WrapperComponent } = props
    return (
      <WrapperComponent {...props}>
        <V1Section {...props} />
      </WrapperComponent>
    )
  }

  return {
    isEmpty: (_props: PresentationIsEmptyProps) => false,
    Section: React.memo(PartnerBlock, compareBlockProps)
  }
}
